import { useRef, useState } from "react";
import { layerZAt } from "../lib/display/layerHelpers.js";
import { displaySize } from "../constants.js";
import ImageUploadButton from "./ImageUploadButton.js";
import SequenceEditor from "./ComposeSequence.js";
import LayerAnimationControls from "./ComposeAnimation.js";
import { ColorField } from "./ComposeFields.js";
import RichTextDialog from "./RichTextDialog.js";
import { resolveFontSize } from "../lib/display/runtime.js";
import "./ComposeFields.css";
import "./ComposeLayers.css";

// Advanced mode has no template-layout-derived budget the way simple
// mode's dynamic, box-size-based one works (there's no layouts[sizeId]
// concept once positioning is freeform) - a flat, arbitrary limit for
// CTA specifically, per explicit request, rather than no limit at all.
const CTA_CHAR_LIMIT = 30;

const ROLE_LABEL = {
  headline: "Headline",
  subline: "Subline",
  cta: "CTA",
  logo: "Logo",
  background: "Background",
  custom: "Custom",
};

export default function LayersPanel({
  doc,
  activeSizeId,
  selectedLayerId,
  references,
  regulated,
  editable,
  palette,
  ctx,
  onSelectLayer,
  onAddLayer,
  onDeleteLayer,
  onSetLayerOrder,
  onUpdateLayer,
  onSetLayerBox,
  onSetBackground,
  selectedSize,
  onSetComplianceLine,
  complianceStale,
  onUploadImage,
  isUploadingImage,
  uploadError,
}) {
  const layers = [...(doc.layers ?? [])].sort(
    (a, b) => layerZAt(b, activeSizeId) - layerZAt(a, activeSizeId)
  );
  const selectedLayer = layers.find((l) => l.id === selectedLayerId) ?? null;
  const pickableImages = (references ?? []).filter((r) => r.kind === "product" || r.kind === "example");
  const activeSizeInfo = displaySize(activeSizeId);

  const dragIdRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  function beginDrag(id) {
    dragIdRef.current = id;
    setDragId(id);
  }
  function endDrag() {
    dragIdRef.current = null;
    setDragId(null);
    setOverId(null);
  }
  function dropOn(targetId) {
    const from = dragIdRef.current;
    if (from && from !== targetId) {
      const ids = layers.map((l) => l.id);
      const fromIdx = ids.indexOf(from);
      const toIdx = ids.indexOf(targetId);
      if (fromIdx >= 0 && toIdx >= 0) {
        ids.splice(fromIdx, 1);
        ids.splice(toIdx, 0, from);
        onSetLayerOrder(ids, activeSizeId);
      }
    }
    endDrag();
  }

  return (
    <div className="layers-panel">
      <section className="layers-panel__section">
        <h3 className="layers-panel__section-title">Background image</h3>
        <ImageGrid
          selected={
            (selectedSize
              ? doc.backgroundBySize?.[selectedSize] ?? doc.advancedBackground
              : doc.advancedBackground) ?? null
          }
          images={pickableImages}
          onPick={onSetBackground}
          onUploadImage={onUploadImage}
          isUploadingImage={isUploadingImage}
          uploadError={uploadError}
          disabled={!editable}
        />
      </section>

      <section className="layers-panel__section">
        <h3 className="layers-panel__section-title">Compliance line</h3>
        {complianceStale ? (
          <p className="layers-panel__stale-notice">
            This asset's compliance line differs from the brand's current one. Re-export to pick up the latest text.
          </p>
        ) : null}
        {regulated ? (
          <>
            <span className="layers-panel__locked-badge">LOCKED — regulated industry</span>
            <p className="layers-panel__locked-value">{doc.slots.complianceLine}</p>
            <p className="layers-panel__hint">
              Rendered as a locked strip at the bottom of every size — not an editable layer.
            </p>
          </>
        ) : (
          <input
            type="text"
            value={doc.slots.complianceLine ?? ""}
            maxLength={160}
            placeholder="Optional legal line"
            disabled={!editable}
            onChange={(e) => onSetComplianceLine(e.target.value === "" ? null : e.target.value)}
            className="layers-panel__input"
          />
        )}
      </section>

      <section className="layers-panel__section">
        <div className="layers-panel__list-header">
          <h3 className="layers-panel__section-title">Elements</h3>
        </div>
        <div className="layers-panel__add-buttons">
          <button type="button" disabled={!editable} onClick={() => onAddLayer("text", "custom")} className="layers-panel__add-btn">
            + Text
          </button>
          <button type="button" disabled={!editable} onClick={() => onAddLayer("text", "cta")} className="layers-panel__add-btn">
            + CTA
          </button>
          <button type="button" disabled={!editable} onClick={() => onAddLayer("image", "custom")} className="layers-panel__add-btn">
            + Image
          </button>
        </div>

        {layers.length === 0 ? (
          <p className="layers-panel__hint">Empty canvas — add a text, CTA or image element to begin.</p>
        ) : (
          <p className="layers-panel__hint">
            Drag <strong>⠿</strong> to reorder — top of the list is in front.
          </p>
        )}

        <ul className="layers-panel__list">
          {layers.map((layer) => {
            const here = layer.boxes[activeSizeId];
            const isSel = layer.id === selectedLayerId;
            const placed = here !== undefined && !here.hidden;
            return (
              <li
                key={layer.id}
                onDragOver={(e) => {
                  if (dragIdRef.current) {
                    e.preventDefault();
                    setOverId(layer.id);
                  }
                }}
                onDrop={() => dropOn(layer.id)}
                className={`layers-panel__row${isSel ? " layers-panel__row--selected" : ""}${
                  overId === layer.id && dragId ? " layers-panel__row--over" : ""
                }${dragId === layer.id ? " layers-panel__row--dragging" : ""}`}
              >
                <span
                  draggable={editable}
                  onDragStart={() => beginDrag(layer.id)}
                  onDragEnd={endDrag}
                  title="Drag to reorder (top = in front)"
                  className="layers-panel__handle"
                  aria-hidden
                >
                  ⠿
                </span>
                <button
                  type="button"
                  onClick={() => onSelectLayer(isSel ? null : layer.id)}
                  className="layers-panel__row-main"
                >
                  <span className="layers-panel__row-type">{layer.type === "image" ? "IMG" : "TXT"}</span>
                  <span className="layers-panel__row-label">{layer.name ?? ROLE_LABEL[layer.role] ?? layer.role}</span>
                  {layer.type === "text" ? (
                    <span className="layers-panel__row-preview">{layer.text}</span>
                  ) : null}
                </button>
                {here ? (
                  <button
                    type="button"
                    disabled={!editable}
                    onClick={() => onSetLayerBox(layer.id, activeSizeId, { hidden: !here.hidden })}
                    title={placed ? "Hide on this size" : "Show on this size"}
                    aria-label={placed ? "Hide on this size" : "Show on this size"}
                    className="layers-panel__visibility-btn"
                  >
                    {placed ? "👁" : "🚫"}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={!editable || !activeSizeInfo}
                    onClick={() =>
                      activeSizeInfo &&
                      onSetLayerBox(layer.id, activeSizeId, {
                        x: Math.round(activeSizeInfo.w * 0.1),
                        y: Math.round(activeSizeInfo.h * 0.4),
                        w: Math.round(activeSizeInfo.w * 0.5),
                        h: Math.round(activeSizeInfo.h * 0.2),
                        hidden: false,
                      })
                    }
                    className="layers-panel__place-btn"
                  >
                    + place
                  </button>
                )}
                <button
                  type="button"
                  disabled={!editable}
                  onClick={() => onDeleteLayer(layer.id)}
                  aria-label="Delete layer"
                  className="layers-panel__icon-btn layers-panel__icon-btn--danger"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {selectedLayer ? (
        <LayerEditor
          layer={selectedLayer}
          sizeId={activeSizeId}
          images={pickableImages}
          editable={editable}
          palette={palette}
          ctx={ctx}
          defaultBgColor={doc.slots.ctaColor}
          allowInfinite={doc.deliveryProfile === "self_hosted"}
          onUpdateLayer={onUpdateLayer}
          onSetLayerBox={onSetLayerBox}
          onUploadImage={onUploadImage}
          isUploadingImage={isUploadingImage}
          uploadError={uploadError}
        />
      ) : null}
    </div>
  );
}

export function LayerEditor({
  layer,
  sizeId,
  images,
  editable,
  palette,
  ctx,
  defaultBgColor,
  allowInfinite,
  onUpdateLayer,
  onSetLayerBox,
  onUploadImage,
  isUploadingImage,
  uploadError,
}) {
  const box = layer.boxes[sizeId];
  const [textDialogOpen, setTextDialogOpen] = useState(false);

  return (
    <section className="layers-panel__section layers-panel__editor">
      <h3 className="layers-panel__section-title">
        Editing: {layer.name ?? ROLE_LABEL[layer.role] ?? layer.role} ({sizeId})
      </h3>

      {!box ? (
        <p className="layers-panel__hint">
          This layer isn't placed on {sizeId} yet.{" "}
          <button
            type="button"
            disabled={!editable}
            className="layers-panel__link-btn"
            onClick={() => onSetLayerBox(layer.id, sizeId, { x: 20, y: 20, w: 100, h: 30 })}
          >
            Add it here
          </button>
        </p>
      ) : (
        <>
          <p className="layers-panel__hint">
            Drag on the canvas to move; drag a handle to resize.
          </p>

          <div className="layers-panel__content-toggle">
            <span className="layers-panel__content-toggle-label">Content</span>
            <div className="layers-panel__toggle-group">
              <button
                type="button"
                aria-pressed={!layer.sequence}
                disabled={!editable}
                onClick={() =>
                  layer.sequence &&
                  onUpdateLayer(layer.id, (l) => {
                    const first = l.sequence?.frames[0];
                    const next = { ...l };
                    delete next.sequence;
                    if (l.type === "text") next.text = first?.text ?? l.text;
                    else next.imageSource = first?.imageSource ?? l.imageSource;
                    return next;
                  })
                }
                className="layers-panel__toggle-btn"
              >
                Single
              </button>
              <button
                type="button"
                aria-pressed={!!layer.sequence}
                disabled={!editable}
                title="Cycle several texts (or images) in this one element"
                onClick={() =>
                  !layer.sequence &&
                  onUpdateLayer(layer.id, (l) => ({
                    ...l,
                    sequence: {
                      cycleDuration: 5,
                      loops: 1,
                      frames: [
                        {
                          text: l.type === "text" ? (l.text ?? "First message") : undefined,
                          imageSource: l.type === "image" ? l.imageSource : undefined,
                          in: { effect: "fade", delay: 0, duration: 0.5 },
                          out: { effect: "fade", start: 2.2, duration: 0.5 },
                        },
                        {
                          text: l.type === "text" ? "Second message" : undefined,
                          in: { effect: "fade", delay: 2.7, duration: 0.5 },
                        },
                      ],
                    },
                  }))
                }
                className="layers-panel__toggle-btn"
              >
                Sequence
              </button>
            </div>
          </div>

          {layer.type === "text" ? (
            <>
              {!layer.sequence ? (
                <div className="layers-panel__field">
                  <div className="layers-panel__field-top">
                    <span>Text</span>
                    {layer.role === "cta" ? (
                      <span
                        className={`editing-panel__count${
                          (layer.text ?? "").length > CTA_CHAR_LIMIT ? " editing-panel__count--over" : ""
                        }`}
                      >
                        {(layer.text ?? "").length}/{CTA_CHAR_LIMIT}
                      </span>
                    ) : null}
                  </div>
                  <div className="layers-panel__text-preview">
                    {(layer.text ?? "").split("\n").map((line, i, arr) => (
                      <span key={i}>
                        {line || <em>(empty line)</em>}
                        {i < arr.length - 1 ? <br /> : null}
                      </span>
                    ))}
                    {(layer.words?.length > 0 && layer.words.some((w) => w.color)) || layer.colorRanges?.length > 0 ? (
                      <span className="layers-panel__text-preview-color-dot" aria-label="Has colored text" />
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={!editable}
                    onClick={() => setTextDialogOpen(true)}
                    className="layers-panel__link-btn"
                  >
                    Edit text →
                  </button>
                  {textDialogOpen ? (
                    <RichTextDialog
                      title={`Editing ${ROLE_LABEL[layer.role] ?? layer.role} text`}
                      initialText={layer.text ?? ""}
                      initialColorRanges={layer.colorRanges ?? []}
                      initialWords={layer.words ?? []}
                      paletteColors={palette ?? []}
                      onSave={({ text, colorRanges, words }) => {
                        onUpdateLayer(layer.id, { text, colorRanges, words });
                        setTextDialogOpen(false);
                      }}
                      onClose={() => setTextDialogOpen(false)}
                    />
                  ) : null}
                </div>
              ) : null}
              <div className="layers-panel__font-field">
                <div className="layers-panel__font-field-top">
                  <span>Font size</span>
                  <button
                    type="button"
                    disabled={!editable}
                    onClick={() =>
                      onUpdateLayer(layer.id, (l) => {
                        const isManual = l.fontSizeAuto === false;
                        if (isManual) {
                          // Switching back to auto-fit - clear just this
                          // size's override, not the whole map (other
                          // sizes may have legitimate, unrelated entries
                          // from the conversion-time freeze). Otherwise
                          // fontSizeBySize would keep taking priority over
                          // fontSizeAuto and silently defeat this toggle.
                          const nextBySize = { ...l.fontSizeBySize };
                          delete nextBySize[sizeId];
                          return { ...l, fontSizeAuto: true, fontSizeBySize: nextBySize };
                        }
                        // Seed the manual value with what auto-fit currently
                        // renders for THIS size specifically, so switching
                        // to manual doesn't jump the size and doesn't
                        // affect any other size. A sequence layer has no
                        // single "the" text - its frames each fit
                        // independently - so the first frame's text is
                        // used as a representative sample.
                        const sampleText = l.sequence ? (l.sequence.frames[0]?.text ?? "") : (l.text ?? "");
                        const live = box
                          ? resolveFontSize({ ...l, fontSizeAuto: true }, sampleText, box.w, box.h, sizeId, ctx)
                          : (l.fontSize ?? 14);
                        return {
                          ...l,
                          fontSizeAuto: false,
                          fontSizeBySize: { ...l.fontSizeBySize, [sizeId]: live },
                        };
                      })
                    }
                    className="layers-panel__link-btn"
                  >
                    {layer.fontSizeAuto === false ? "Auto-fit" : "Set manually"}
                  </button>
                </div>
                {layer.fontSizeAuto === false ? (
                  <NumberField
                    label=""
                    value={layer.fontSizeBySize?.[sizeId] ?? layer.fontSize ?? 14}
                    disabled={!editable}
                    onChange={(v) =>
                      onUpdateLayer(layer.id, (l) => ({
                        ...l,
                        fontSizeBySize: { ...l.fontSizeBySize, [sizeId]: v },
                      }))
                    }
                  />
                ) : layer.sequence ? (
                  <p className="layers-panel__font-auto-value">
                    Fits each frame's own text automatically — sizes may vary frame to frame.
                  </p>
                ) : (
                  <p className="layers-panel__font-auto-value">
                    {box ? resolveFontSize(layer, layer.text ?? "", box.w, box.h, sizeId, ctx) : (layer.fontSize ?? 14)}px
                    — fits this size automatically
                  </p>
                )}
              </div>
              <ColorField
                label="Text color"
                value={layer.color ?? "#111111"}
                palette={palette ?? []}
                editable={editable}
                onChange={(v) => onUpdateLayer(layer.id, { color: v })}
              />
              <label className="layers-panel__field">
                <span>Align</span>
                <select
                  value={layer.align ?? "left"}
                  disabled={!editable}
                  onChange={(e) => onUpdateLayer(layer.id, { align: e.target.value })}
                  className="layers-panel__input"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                </select>
              </label>
              <div className="layers-panel__bg-field">
                <div className="layers-panel__bg-field-top">
                  <span>Button background</span>
                  <button
                    type="button"
                    disabled={!editable}
                    onClick={() =>
                      onUpdateLayer(layer.id, (l) => ({ ...l, bg: l.bg ? null : defaultBgColor }))
                    }
                    className="layers-panel__link-btn"
                  >
                    {layer.bg ? "Remove" : "Add"}
                  </button>
                </div>
                {layer.bg ? (
                  <ColorField
                    label=""
                    value={layer.bg}
                    palette={palette ?? []}
                    editable={editable}
                    onChange={(v) => onUpdateLayer(layer.id, { bg: v })}
                  />
                ) : (
                  <p className="layers-panel__hint">No fill — renders as plain text.</p>
                )}
              </div>
            </>
          ) : (
            <>
              {!layer.sequence ? (
                <>
                  <p className="layers-panel__field-label">Image</p>
                  <ImageGrid
                    selected={layer.imageSource ?? null}
                    images={images}
                    onPick={(source) => onUpdateLayer(layer.id, { imageSource: source })}
                    onUploadImage={onUploadImage}
                    isUploadingImage={isUploadingImage}
                    uploadError={uploadError}
                    disabled={!editable}
                  />
                </>
              ) : null}
              <label className="layers-panel__field">
                <span>Fit</span>
                <select
                  value={layer.fit ?? "cover"}
                  disabled={!editable}
                  onChange={(e) => onUpdateLayer(layer.id, { fit: e.target.value })}
                  className="layers-panel__input"
                >
                  <option value="cover">Cover</option>
                  <option value="contain">Contain</option>
                </select>
              </label>
            </>
          )}

          {layer.sequence ? (
            <SequenceEditor
              layer={layer}
              editable={editable}
              images={images}
              allowInfinite={allowInfinite}
              onUpdateLayer={onUpdateLayer}
              onUploadImage={onUploadImage}
              isUploadingImage={isUploadingImage}
              uploadError={uploadError}
              palette={palette}
            />
          ) : (
            <LayerAnimationControls
              layer={layer}
              editable={editable}
              allowInfinite={allowInfinite}
              onUpdateLayer={onUpdateLayer}
            />
          )}
        </>
      )}
    </section>
  );
}

export function NumberField({ label, value, disabled, onChange }) {
  return (
    <label className="layers-panel__field layers-panel__field--number">
      <span>{label}</span>
      <input
        type="number"
        value={Math.round(value)}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="layers-panel__input"
      />
    </label>
  );
}

export function ImageGrid({ selected, images, onPick, onUploadImage, isUploadingImage, uploadError, disabled }) {
  return (
    <div>
      <ImageUploadButton
        kind="example"
        onUploadImage={onUploadImage}
        isUploadingImage={isUploadingImage}
        onUploaded={onPick}
        disabled={disabled}
      />
      {uploadError ? <p className="layers-panel__error">{uploadError}</p> : null}

      {images.length === 0 ? (
        <p className="layers-panel__hint">No product/example references yet.</p>
      ) : (
        <div className="layers-panel__image-grid">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onPick(null)}
            className={`layers-panel__image-btn${!selected ? " layers-panel__image-btn--selected" : ""}`}
          >
            <span className="layers-panel__none-label">None</span>
          </button>
          {images.map((img) => {
            const isSelected = selected?.kind === "reference" && selected?.id === img.id;
            return (
              <button
                key={img.id}
                type="button"
                disabled={disabled}
                onClick={() => onPick({ kind: "reference", id: img.id })}
                className={`layers-panel__image-btn${isSelected ? " layers-panel__image-btn--selected" : ""}`}
              >
                <img src={img.url} alt={img.label ?? "Reference"} className="layers-panel__image-img" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}